export const VACATION_API_PATHS = {
  MY_BALANCE: '/common/vacations/me/balance',
  MY_AVAILABILITY: '/common/vacations/me/availability',
  MY_ENTRIES: '/common/vacations/me/entries',
  BOOKING_PREVIEW: '/common/vacation-bookings/preview',
  BOOKINGS: '/common/vacation-bookings',
  BOOKING_CONFIRM: (bookingId: string) =>
    '/common/vacation-bookings/' + encodeURIComponent(bookingId) + '/confirm',
  BOOKING_BY_ID: (bookingId: string) =>
    '/common/vacation-bookings/' + encodeURIComponent(bookingId),
  GROUP_CALENDAR: (groupId: string) =>
    '/common/groups/' + encodeURIComponent(groupId) + '/vacation-calendar',
  GROUP_PRESENCE: (groupId: string) =>
    '/common/groups/' + encodeURIComponent(groupId) + '/presence',
  ADMIN_POLICY: '/admin/setting/vacation-policy'
};

export const VACATION_QUERY_PARAM = {
  AS_OF: 'asOf',
  FROM: 'from',
  TO: 'to',
  PAGE: 'page',
  SIZE: 'size'
};

export const VACATION_BOOKING_STATUS: {
  RESERVED: 'RESERVED';
  CONFIRMING: 'CONFIRMING';
  CONFIRMED: 'CONFIRMED';
  MATERIALIZED: 'MATERIALIZED';
  CANCELLED: 'CANCELLED';
  EXPIRED: 'EXPIRED';
} = {
  RESERVED: 'RESERVED',
  CONFIRMING: 'CONFIRMING',
  CONFIRMED: 'CONFIRMED',
  MATERIALIZED: 'MATERIALIZED',
  CANCELLED: 'CANCELLED',
  EXPIRED: 'EXPIRED'
};

export type VacationBookingStatus =
  typeof VACATION_BOOKING_STATUS[keyof typeof VACATION_BOOKING_STATUS];

export const VACATION_ENTRY_KIND: {
  BOOKING: 'BOOKING';
  VACATION: 'VACATION';
} = {
  BOOKING: 'BOOKING',
  VACATION: 'VACATION'
};

export type VacationEntryKind =
  typeof VACATION_ENTRY_KIND[keyof typeof VACATION_ENTRY_KIND];

export const VACATION_ENTRY_STATUS: {
  COORDINATION: 'COORDINATION';
  APPROVED: 'APPROVED';
  COMPLETED: 'COMPLETED';
  REJECTED: 'REJECTED';
  UNKNOWN: 'UNKNOWN';
} = {
  COORDINATION: 'COORDINATION',
  APPROVED: 'APPROVED',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
  UNKNOWN: 'UNKNOWN'
};

export type VacationEntryStatus =
  VacationBookingStatus |
  typeof VACATION_ENTRY_STATUS[keyof typeof VACATION_ENTRY_STATUS];

export const VACATION_TYPE: {
  PAID_LEAVE: 'PAID_LEAVE';
  UNPAID_LEAVE: 'UNPAID_LEAVE';
  SICK_LEAVE: 'SICK_LEAVE';
  BUSINESS_TRIP: 'BUSINESS_TRIP';
} = {
  PAID_LEAVE: 'PAID_LEAVE',
  UNPAID_LEAVE: 'UNPAID_LEAVE',
  SICK_LEAVE: 'SICK_LEAVE',
  BUSINESS_TRIP: 'BUSINESS_TRIP'
};

export type VacationType = typeof VACATION_TYPE[keyof typeof VACATION_TYPE];

export const VACATION_SEASON: {
  SPRING: 'SPRING';
  SUMMER: 'SUMMER';
  AUTUMN: 'AUTUMN';
  WINTER: 'WINTER';
} = {
  SPRING: 'SPRING',
  SUMMER: 'SUMMER',
  AUTUMN: 'AUTUMN',
  WINTER: 'WINTER'
};

export type VacationSeason =
  typeof VACATION_SEASON[keyof typeof VACATION_SEASON];

export const PUBLIC_ABSENCE_STATUS: {
  VACATION: 'VACATION';
  SICK_LEAVE: 'SICK_LEAVE';
  BUSINESS_TRIP: 'BUSINESS_TRIP';
} = {
  VACATION: 'VACATION',
  SICK_LEAVE: 'SICK_LEAVE',
  BUSINESS_TRIP: 'BUSINESS_TRIP'
};

export type PublicAbsenceStatus =
  typeof PUBLIC_ABSENCE_STATUS[keyof typeof PUBLIC_ABSENCE_STATUS];

export const PUBLIC_ABSENCE_STATUSES: PublicAbsenceStatus[] = [
  PUBLIC_ABSENCE_STATUS.VACATION,
  PUBLIC_ABSENCE_STATUS.SICK_LEAVE,
  PUBLIC_ABSENCE_STATUS.BUSINESS_TRIP
];

export const VACATION_ERROR_CODE: {
  APPROVAL_ROUTE_UNVERIFIED: 'VACATION_APPROVAL_ROUTE_UNVERIFIED';
  APPROVAL_ROUTE_INVALID: 'VACATION_APPROVAL_ROUTE_INVALID';
  APPROVAL_RECIPIENT_UNAVAILABLE: 'VACATION_APPROVAL_RECIPIENT_UNAVAILABLE';
  APPROVAL_SELF_UNRESOLVED: 'VACATION_APPROVAL_SELF_UNRESOLVED';
  BALANCE_UNAVAILABLE: 'VACATION_BALANCE_UNAVAILABLE';
  DATE_INVALID: 'VACATION_DATE_INVALID';
  OVERLAP: 'VACATION_OVERLAP';
  BALANCE_EXCEEDED: 'VACATION_BALANCE_EXCEEDED';
  SEASON_ALREADY_USED: 'VACATION_SEASON_ALREADY_USED';
  CAPACITY_EXCEEDED: 'VACATION_CAPACITY_EXCEEDED';
  CONFIRMATION_NOT_OPEN: 'VACATION_CONFIRMATION_NOT_OPEN';
  CONFIRMATION_EXPIRED: 'VACATION_CONFIRMATION_EXPIRED';
  BOOKING_NOT_FOUND: 'VACATION_BOOKING_NOT_FOUND';
  BOOKING_REQUIRED: 'VACATION_BOOKING_REQUIRED';
  DEPARTMENT_CHANGED: 'VACATION_DEPARTMENT_CHANGED';
  REQUEST_INVALID: 'VACATION_REQUEST_INVALID';
  ACCESS_DENIED: 'VACATION_ACCESS_DENIED';
  CONCURRENT_UPDATE: 'VACATION_CONCURRENT_UPDATE';
  PLANFIX_UNAVAILABLE: 'PLANFIX_TEMPORARILY_UNAVAILABLE';
} = {
  APPROVAL_ROUTE_UNVERIFIED: 'VACATION_APPROVAL_ROUTE_UNVERIFIED',
  APPROVAL_ROUTE_INVALID: 'VACATION_APPROVAL_ROUTE_INVALID',
  APPROVAL_RECIPIENT_UNAVAILABLE: 'VACATION_APPROVAL_RECIPIENT_UNAVAILABLE',
  APPROVAL_SELF_UNRESOLVED: 'VACATION_APPROVAL_SELF_UNRESOLVED',
  BALANCE_UNAVAILABLE: 'VACATION_BALANCE_UNAVAILABLE',
  DATE_INVALID: 'VACATION_DATE_INVALID',
  OVERLAP: 'VACATION_OVERLAP',
  BALANCE_EXCEEDED: 'VACATION_BALANCE_EXCEEDED',
  SEASON_ALREADY_USED: 'VACATION_SEASON_ALREADY_USED',
  CAPACITY_EXCEEDED: 'VACATION_CAPACITY_EXCEEDED',
  CONFIRMATION_NOT_OPEN: 'VACATION_CONFIRMATION_NOT_OPEN',
  CONFIRMATION_EXPIRED: 'VACATION_CONFIRMATION_EXPIRED',
  BOOKING_NOT_FOUND: 'VACATION_BOOKING_NOT_FOUND',
  BOOKING_REQUIRED: 'VACATION_BOOKING_REQUIRED',
  DEPARTMENT_CHANGED: 'VACATION_DEPARTMENT_CHANGED',
  REQUEST_INVALID: 'VACATION_REQUEST_INVALID',
  ACCESS_DENIED: 'VACATION_ACCESS_DENIED',
  CONCURRENT_UPDATE: 'VACATION_CONCURRENT_UPDATE',
  PLANFIX_UNAVAILABLE: 'PLANFIX_TEMPORARILY_UNAVAILABLE'
};

export type VacationErrorCode =
  typeof VACATION_ERROR_CODE[keyof typeof VACATION_ERROR_CODE];

export const VACATION_WARNING_CODE: {
  FIRST_WORK_YEAR: 'VACATION_FIRST_WORK_YEAR';
  SHORT_PART: 'VACATION_SHORT_PART';
  OVERLAP: 'VACATION_OVERLAP';
  BALANCE_EXCEEDED: 'VACATION_BALANCE_EXCEEDED';
  SEASON_ALREADY_USED: 'VACATION_SEASON_ALREADY_USED';
  CAPACITY_EXCEEDED: 'VACATION_CAPACITY_EXCEEDED';
} = {
  FIRST_WORK_YEAR: 'VACATION_FIRST_WORK_YEAR',
  SHORT_PART: 'VACATION_SHORT_PART',
  OVERLAP: 'VACATION_OVERLAP',
  BALANCE_EXCEEDED: 'VACATION_BALANCE_EXCEEDED',
  SEASON_ALREADY_USED: 'VACATION_SEASON_ALREADY_USED',
  CAPACITY_EXCEEDED: 'VACATION_CAPACITY_EXCEEDED'
};

export type VacationWarningCode =
  typeof VACATION_WARNING_CODE[keyof typeof VACATION_WARNING_CODE];



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/vacation/vacation-api.constants.ts